import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Building2, Users, ClipboardCheck, Plus, ArrowRight, AlertCircle, Trash2, AlertTriangle } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-green-100 text-green-700",
  trial: "bg-blue-100 text-blue-700",
  past_due: "bg-amber-100 text-amber-700",
  canceled: "bg-slate-200 text-slate-600",
};

function slugify(s) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}


function StatCard({ icon: Icon, label, value, color = "text-slate-900", testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className={`mt-1 text-3xl font-bold ${color}`} data-testid={testId}>{value}</p>
    </div>
  );
}

const EMPTY = {
  name: "", slug: "", contact_email: "", phone: "", address: "", tagline: "",
  admin_name: "", admin_email: "", admin_password: "", template_source: "starter",
};

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [clinics, setClinics] = useState([]);
  const [stats, setStats] = useState({ clinics: 0, patients: 0, submissions: 0 });
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/super/clinics"), api.get("/super/stats")]);
      setClinics(c.data);
      setStats(s.data);
    } catch (e) {
      toast.error("Could not load platform data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetDemo = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/super/reset-patients");
      const d = data.deleted || {};
      toast.success(`Demo reset — removed ${d.patients || 0} patient${d.patients === 1 ? "" : "s"} and their paperwork.`);
      setResetOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not reset demo data.");
    } finally {
      setResetting(false);
    }
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setError("");
    const payload = { ...form, slug: form.slug || slugify(form.name) };
    if (!payload.name || !payload.contact_email || !payload.admin_email || payload.admin_password.length < 6) {
      setError("Please fill Nimbus name, contact email, admin email, and a password (6+ chars).");
      return;
    }
    setSaving(true);
    try {
      await api.post("/super/clinics", payload);
      toast.success(`${payload.name} added.`);
      setOpen(false);
      setForm(EMPTY);
      setSlugTouched(false);
      load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Could not create Nimbus.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Platform Overview</h1>
            <p className="mt-1 text-sm text-slate-500">Manage Nimbus accounts, subscriptions and paperwork templates.</p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={resetOpen} onOpenChange={setResetOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="reset-demo-btn">
                  <Trash2 className="mr-1.5 h-4 w-4" /> Reset demo
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="h-5 w-5" /> Reset demo data</DialogTitle>
                  <DialogDescription>
                    This permanently removes <strong>all patient accounts</strong> and their paperwork, saved profiles, and check-in history so your demo starts clean. Your Nimbus accounts, templates, and admin logins are kept. This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2 sm:gap-2">
                  <Button variant="outline" onClick={() => setResetOpen(false)} data-testid="reset-demo-cancel">Cancel</Button>
                  <Button className="bg-red-600 hover:bg-red-700" onClick={resetDemo} disabled={resetting} data-testid="reset-demo-confirm">
                    {resetting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />} Wipe patients
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#0F52BA] hover:bg-[#0B3D8A]" data-testid="add-clinic-btn">
                  <Plus className="mr-1.5 h-4 w-4" /> Add Nimbus
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add a new Nimbus</DialogTitle>
                <DialogDescription>Create a Nimbus tenant with its own paperwork template and admin login.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {error && (
                  <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="add-clinic-error">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{error}</span>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Nimbus name</Label>
                    <Input value={form.name} onChange={(e) => { setField("name", e.target.value); if (!slugTouched) setField("slug", slugify(e.target.value)); }} data-testid="clinic-name" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">URL slug</Label>
                    <Input value={form.slug} onChange={(e) => { setSlugTouched(true); setField("slug", slugify(e.target.value)); }} data-testid="clinic-slug" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Tagline</Label>
                    <Input value={form.tagline} onChange={(e) => setField("tagline", e.target.value)} data-testid="clinic-tagline" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Contact email</Label>
                    <Input type="email" value={form.contact_email} onChange={(e) => setField("contact_email", e.target.value)} data-testid="clinic-contact-email" />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone</Label>
                    <Input value={form.phone} onChange={(e) => setField("phone", e.target.value)} data-testid="clinic-phone" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Address</Label>
                    <Input value={form.address} onChange={(e) => setField("address", e.target.value)} data-testid="clinic-address" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Paperwork template</Label>
                    <Select value={form.template_source} onValueChange={(v) => setField("template_source", v)}>
                      <SelectTrigger data-testid="clinic-template-source"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="starter">Starter (generic new-patient form)</SelectItem>
                        {clinics.map((c) => (
                          <SelectItem key={c.clinic_id} value={`copy:${c.clinic_id}`}>Copy from: {c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Nimbus admin login</p>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Admin name</Label>
                      <Input value={form.admin_name} onChange={(e) => setField("admin_name", e.target.value)} data-testid="clinic-admin-name" />
                    </div>
                    <div>
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Admin email</Label>
                      <Input type="email" value={form.admin_email} onChange={(e) => setField("admin_email", e.target.value)} data-testid="clinic-admin-email" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="mb-1.5 block text-sm font-semibold text-slate-700">Admin password</Label>
                      <Input type="text" value={form.admin_password} onChange={(e) => setField("admin_password", e.target.value)} data-testid="clinic-admin-password" />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button className="bg-[#0F52BA] hover:bg-[#0B3D8A]" onClick={submit} disabled={saving} data-testid="create-clinic-submit">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Plus className="mr-1.5 h-4 w-4" />} Create Nimbus
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard icon={Building2} label="Nimbus accounts" value={stats.clinics} testId="stat-clinics" />
          <StatCard icon={Building2} label="Active" value={stats.active_clinics ?? "—"} color="text-[#0F52BA]" testId="stat-active-clinics" />
          <StatCard icon={Building2} label="On trial" value={clinics.filter((c) => c.subscription_status === "trial").length} color="text-amber-600" testId="stat-trials" />
          <StatCard icon={Users} label="Patients" value={stats.patients} testId="stat-patients" />
          <StatCard icon={ClipboardCheck} label="Submissions" value={stats.submissions} color="text-green-600" testId="stat-submissions" />
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-5 py-3">
            <h2 className="font-semibold text-slate-900">Nimbus accounts</h2>
          </div>
          {loading ? (
            <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#0F52BA]" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nimbus</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead>Trial ends</TableHead>
                  <TableHead>Signed up</TableHead>
                  <TableHead>Patients</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Manage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinics.map((c) => (
                  <TableRow key={c.clinic_id} data-testid={`clinic-row-${c.slug}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-400">{c.tagline || c.slug}</div>
                    </TableCell>
                    <TableCell>
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[c.subscription_status] || "bg-slate-100 text-slate-600"}`}>
                        {c.subscription_status}
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-700" data-testid={`trial-${c.slug}`}>
                      {c.subscription_status === "trial"
                        ? <span className={c.trial_days_left <= 3 ? "font-semibold text-red-600" : "text-amber-600"}>{fmtDate(c.trial_ends_at)} ({c.trial_days_left ?? 0}d)</span>
                        : <span className="text-slate-300">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-500">{fmtDate(c.created_at)}</TableCell>
                    <TableCell className="text-slate-700">{c.stats?.patients ?? 0}</TableCell>
                    <TableCell className="text-slate-700">{c.stats?.submitted ?? 0}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/super/clinics/${c.clinic_id}`)} data-testid={`manage-${c.slug}`}>
                        Manage <ArrowRight className="ml-1 h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
